/**
 * 图同步 Web Worker Hook
 *
 * 使用 Web Worker 在后台线程处理耗时的图树同步操作
 * 避免阻塞主线程，提升交互响应性
 */

import { useRef, useCallback, useEffect, useState } from 'react';
import type { OntologyGraphTree, OPMGraphData } from '../../../types/opm';

interface WorkerMessage {
  type: 'SUCCESS' | 'ERROR' | 'READY';
  requestId?: string;
  result?: any;
  error?: string;
  elapsed?: number;
}

interface PendingRequest {
  resolve: (value: any) => void;
  reject: (error: Error) => void;
  startTime: number;
}

export const useGraphSyncWorker = () => {
  const workerRef = useRef<Worker | null>(null);
  const pendingRequestsRef = useRef<Map<string, PendingRequest>>(new Map());
  const requestIdCounterRef = useRef(0);
  const [isReady, setIsReady] = useState(false);
  // 使用 ref 跟踪状态，避免闭包问题
  const isReadyRef = useRef(false);

  // 初始化 Worker
  useEffect(() => {
    try {
      // 使用 Vite 的 Worker 导入语法
      workerRef.current = new Worker(
        new URL('../workers/graphSync.worker.ts', import.meta.url),
        { type: 'module' }
      );

      workerRef.current.onmessage = (event: MessageEvent<WorkerMessage>) => {
        const { type, requestId, result, error, elapsed } = event.data;

        if (type === 'READY') {
          setIsReady(true);
          isReadyRef.current = true;
          console.log('✅ GraphSync Worker 已就绪');
          return;
        }

        if (!requestId) return;

        const pending = pendingRequestsRef.current.get(requestId);
        if (!pending) return;

        pendingRequestsRef.current.delete(requestId);

        if (type === 'SUCCESS') {
          const totalTime = performance.now() - pending.startTime;
          if (totalTime > 50) {
            console.log(
              `🔄 Worker 同步完成: 耗时=${elapsed?.toFixed(1)}ms (总计=${totalTime.toFixed(1)}ms)`
            );
          }
          pending.resolve(result);
        } else if (type === 'ERROR') {
          pending.reject(new Error(error || 'Worker error'));
        }
      };

      workerRef.current.onerror = error => {
        console.error('GraphSync Worker 错误:', error);
      };
    } catch (error) {
      console.warn('无法创建 GraphSync Worker，将使用主线程处理:', error);
    }

    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, []);

  // 发送消息到 Worker
  const postMessage = useCallback(
    <T>(type: string, payload: any): Promise<T> => {
      return new Promise((resolve, reject) => {
        // 使用 ref 检查状态，避免闭包问题
        if (!workerRef.current || !isReadyRef.current) {
          // Worker 不可用，直接拒绝
          reject(new Error('Worker not ready'));
          return;
        }

        const requestId = `req_${++requestIdCounterRef.current}`;

        pendingRequestsRef.current.set(requestId, {
          resolve,
          reject,
          startTime: performance.now(),
        });

        workerRef.current.postMessage({
          type,
          payload,
          requestId,
        });

        // 超时处理
        setTimeout(() => {
          const pending = pendingRequestsRef.current.get(requestId);
          if (pending) {
            pendingRequestsRef.current.delete(requestId);
            pending.reject(new Error('Worker request timeout'));
          }
        }, 10000); // 10秒超时
      });
    },
    [isReady]
  );

  // 同步图树（主要接口）
  const syncGraphTree = useCallback(
    async (
      tree: OntologyGraphTree,
      layerId: string,
      graphData: OPMGraphData
    ): Promise<OntologyGraphTree> => {
      // 使用 ref 检查状态，避免闭包问题
      if (!workerRef.current || !isReadyRef.current) {
        // Worker 不可用，返回原始数据
        console.warn('Worker 不可用，跳过图树同步');
        return tree;
      }

      try {
        return await postMessage<OntologyGraphTree>('SYNC_GRAPH_TREE', {
          tree,
          layerId,
          graphData,
        });
      } catch (error) {
        console.error('图树同步失败:', error);
        return tree;
      }
    },
    [postMessage, isReady]
  );

  // 克隆图数据
  const cloneGraphDataAsync = useCallback(
    async (graphData: OPMGraphData): Promise<OPMGraphData> => {
      // 使用 ref 检查状态
      if (!workerRef.current || !isReadyRef.current) {
        // 降级到同步克隆
        return {
          nodes: graphData.nodes.map(n => ({ ...n })),
          edges: graphData.edges.map(e => ({ ...e })),
        };
      }

      try {
        return await postMessage<OPMGraphData>('CLONE_GRAPH_DATA', {
          graphData,
        });
      } catch (error) {
        console.error('图数据克隆失败:', error);
        return graphData;
      }
    },
    [postMessage, isReady]
  );

  return {
    isReady,
    syncGraphTree,
    cloneGraphDataAsync,
  };
};

export default useGraphSyncWorker;
